import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { cpf } from 'cpf-cnpj-validator';
import classNames from 'classnames';

import { AutoComplete } from 'primereact/autocomplete';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import LoadingScreen from 'src/components/LoadingScreen';
import { birthValidation } from 'src/utils/birthValidation';

import { useListUnitysController } from 'src/controllers/Unitys/ListUnitysController';
import { useFormCreateDriver } from 'src/controllers/Driver/CreateDriverController';

interface filteredFieldProps {
	label: string;
	value?: string;
}

const DriverForm: React.FC = () => {
	const { getDriverFormController, handleDriverFormController } = useFormCreateDriver();
	const { getUnityListController } = useListUnitysController();

	const [valueUnity, setValueUnity] = useState('');
	const [filteredUnity, setFilteredUnity] = useState<filteredFieldProps[]>([{ label: '', value: '' }]);

	const [registerFinished, setRegisterFinished] = useState(false);

	const history = useHistory();
	const checkCpfCnpj = cpf.isValid(getDriverFormController.formik.values.CPF || '');
	const isValidDate = birthValidation(getDriverFormController.formik.values.DATANASCIMENTO);

	useEffect(() => {
		if (getDriverFormController.driverResponse?.status === 200) {
			setRegisterFinished(true);
		}
	}, [getDriverFormController.driverResponse]);

	const unitys = getUnityListController.UnitysData.map(unity => {
		return { label: unity.NOME, value: unity.ID };
	});

	const searchUnitys = (event: any) => {
		let filteredUnitys;
		if (!event.query.trim().length) {
			filteredUnitys = [...unitys];
		} else {
			filteredUnitys = unitys.filter(unity => {
				return unity.label.toLowerCase().includes(event.query.toLowerCase());
			});
		}

		setFilteredUnity(filteredUnitys);
	};

	const cnhCategories = [
		{ label: 'A', value: 'A' },
		{ label: 'B', value: 'B' },
		{ label: 'C', value: 'C' },
		{ label: 'D', value: 'D' },
		{ label: 'E', value: 'E' },
	];

	return (
		<div style={{ height: '100%' }} className='card p-align-center p-justify-center'>
			<Toast ref={getDriverFormController.toast} />
			<h5>Cadastro de motorista</h5>
			{getDriverFormController.loading ? <LoadingScreen color='#0388e5' /> : <></>}
			{getDriverFormController.loadingDriverCpf ? <LoadingScreen color='#0388e5' /> : <></>}
			<form className='p-grid p-fluid'>
				<div className='p-field p-col-12 p-md-2'>
					<label htmlFor='cpf'>CPF</label>
					<InputMask
						id='cpf'
						autoClear={false}
						mask='999.999.999-99'
						placeholder='___.___.___-__'
						{...getDriverFormController.formik.getFieldProps('CPF')}
						onChange={e => getDriverFormController.formik.setFieldValue('CPF', e.value)}
						onComplete={(e: any) => {
							handleDriverFormController.searchDriverCpf(e.value);
						}}
						disabled={getDriverFormController.foundDriverCpf || registerFinished}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.CPF &&
								(getDriverFormController.formik.errors.CPF || !checkCpfCnpj),
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.CPF &&
								(getDriverFormController.formik.errors.CPF || !checkCpfCnpj)
							),
						})}>
						{!checkCpfCnpj ? '* Informe CPF válido' : getDriverFormController.formik.errors.CPF}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-8'>
					<label htmlFor='nomeComp'>Nome completo</label>
					<InputText
						id='nomeComp'
						{...getDriverFormController.formik.getFieldProps('NOME')}
						onChange={getDriverFormController.formik.handleChange('NOME')}
						disabled={getDriverFormController.foundDriverCpf || registerFinished}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.NOME &&
								getDriverFormController.formik.errors.NOME,
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.NOME &&
								getDriverFormController.formik.errors.NOME
							),
						})}>
						{getDriverFormController.formik.errors.NOME}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-2'>
					<label htmlFor='dataNac'>Data de nascimento</label>
					<InputMask
						disabled={registerFinished}
						{...getDriverFormController.formik.getFieldProps('DATANASCIMENTO')}
						mask='99/99/9999'
						placeholder='__/__/____'
						onChange={getDriverFormController.formik.handleChange}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.DATANASCIMENTO &&
								(getDriverFormController.formik.errors.DATANASCIMENTO || !isValidDate),
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.DATANASCIMENTO &&
								(getDriverFormController.formik.errors.DATANASCIMENTO || !isValidDate)
							),
						})}>
						{!isValidDate
							? '* Deve ser maior de 18 anos'
							: getDriverFormController.formik.errors.DATANASCIMENTO}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-8'>
					<label htmlFor='numCNH'>Nº de registro CNH</label>
					<InputText
						id='numCNH'
						maxLength={150}
						disabled={registerFinished}
						{...getDriverFormController.formik.getFieldProps('CNH')}
						onChange={getDriverFormController.formik.handleChange('CNH')}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.CNH && getDriverFormController.formik.errors.CNH,
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.CNH && getDriverFormController.formik.errors.CNH
							),
						})}>
						{getDriverFormController.formik.errors.CNH}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-2'>
					<label htmlFor='catCNH'>Categoria CNH</label>
					<MultiSelect
						id='catCNH'
						inputId='catCNH'
						placeholder='Selecionar...'
						{...getDriverFormController.formik.getFieldProps('CATEGORIACNH')}
						onChange={e => {
							getDriverFormController.formik.setFieldValue('CATEGORIACNH', e.value);
						}}
						options={cnhCategories}
						optionLabel='label'
						disabled={registerFinished}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.CATEGORIACNH &&
								getDriverFormController.formik.errors.CATEGORIACNH,
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.CATEGORIACNH &&
								getDriverFormController.formik.errors.CATEGORIACNH
							),
						})}>
						{getDriverFormController.formik.errors.CATEGORIACNH}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-2'>
					<label htmlFor='valCNH'>Validade CNH</label>
					<InputMask
						disabled={registerFinished}
						mask='99/99/9999'
						placeholder='__/__/____'
						{...getDriverFormController.formik.getFieldProps('VALIDADECNH')}
						onChange={e => getDriverFormController.formik.setFieldValue('VALIDADECNH', e.value)}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.VALIDADECNH &&
								getDriverFormController.formik.errors.VALIDADECNH,
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.VALIDADECNH &&
								getDriverFormController.formik.errors.VALIDADECNH
							),
						})}>
						{getDriverFormController.formik.errors.VALIDADECNH}
					</div>
				</div>

				<h5 className='p-col-12'>Cadastro de usuário motorista</h5>

				<div className='p-field p-col-12 p-md-6'>
					<label htmlFor='login'>Email de motorista</label>
					<InputText
						id='login'
						placeholder='exemplo@email.com'
						disabled={registerFinished}
						{...getDriverFormController.formik.getFieldProps('LOGIN')}
						onChange={getDriverFormController.formik.handleChange('LOGIN')}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.LOGIN &&
								getDriverFormController.formik.errors.LOGIN,
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.LOGIN &&
								getDriverFormController.formik.errors.LOGIN
							),
						})}>
						{getDriverFormController.formik.errors.LOGIN}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-6'>
					<label htmlFor='login'>Senha para motorista</label>
					<InputText
						id='senha'
						disabled={registerFinished}
						{...getDriverFormController.formik.getFieldProps('SENHA')}
						onChange={getDriverFormController.formik.handleChange('SENHA')}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.SENHA &&
								getDriverFormController.formik.errors.SENHA,
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.SENHA &&
								getDriverFormController.formik.errors.SENHA
							),
						})}>
						{getDriverFormController.formik.errors.SENHA}
					</div>
				</div>

				<div className='p-field p-col-12'>
					<label htmlFor='unidade'>Unidade</label>
					<AutoComplete
						id='unidade'
						value={valueUnity}
						disabled={registerFinished}
						field='label'
						placeholder='Buscar...'
						onChange={e => {
							setValueUnity(e.value);
							getDriverFormController.formik.setFieldValue(
								'ORGAOUNIDADE',
								filteredUnity.find(unity => {
									return valueUnity.toLowerCase() === unity.label.toLowerCase();
								})?.value || '',
							);
						}}
						onSelect={e => {
							setValueUnity(e.value.label);
							getDriverFormController.formik.setFieldValue('ORGAOUNIDADE', e.value.value);
						}}
						suggestions={filteredUnity}
						completeMethod={searchUnitys}
						dropdown
						dropdownMode='current'
						minLength={2}
						delay={80}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getDriverFormController.formik.touched.ORGAOUNIDADE &&
								getDriverFormController.formik.errors.ORGAOUNIDADE,
							'popup-form-inactive': !(
								getDriverFormController.formik.touched.ORGAOUNIDADE &&
								getDriverFormController.formik.errors.ORGAOUNIDADE
							),
						})}>
						{getDriverFormController.formik.errors.ORGAOUNIDADE}
					</div>
				</div>
			</form>

			<div className='p-fluid p-grid p-justify-between'>
				<div className='p-col-12 p-md-6 p-fluid p-grid'>
					<div className='p-col-12 p-md-5'>
						<Button
							type='button'
							label='Cadastrar'
							onClick={() =>
								checkCpfCnpj && isValidDate ? getDriverFormController.formik.handleSubmit() : {}
							}
						/>
					</div>
					{getDriverFormController.driverResponse?.status === 200 ? (
						<div className='p-col-12 p-md-5'>
							<Button
								icon='pi pi-plus'
								type='button'
								className='p-button-success'
								label='Cadastrar novo'
								onClick={() => {
									getDriverFormController.formik.resetForm();
									setValueUnity('');
									setRegisterFinished(false);
								}}
							/>
						</div>
					) : (
						<></>
					)}
				</div>

				<div className='p-col-12 p-md-6 p-fluid p-grid'>
					<div className='p-col-6'>
						<Button
							type='button'
							label='Limpar'
							onClick={() => {
								getDriverFormController.formik.resetForm();
								setValueUnity('');
								setRegisterFinished(false);
								handleDriverFormController.setFoundDriverCpf(false);
							}}
						/>
					</div>
					<div className='p-col-6'>
						<Button
							type='button'
							className='p-button-secondary'
							label='Voltar para listagem'
							onClick={() => history.goBack()}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DriverForm;
